// Performance optimization utilities for Born Free PWA

export const PERFORMANCE_CONFIG = {
  // Image loading configuration - Restored to full functionality like app.bornfree.io
  MAX_IMAGES_PER_COLLECTION: 50, // Restored to 150 total images (50 per collection)
  THUMBNAIL_BUFFER_SIZE: 4, // Number of extra thumbnails to render outside visible area
  PRELOAD_NEXT_IMAGES: 1, // Only preload 1 image ahead
  
  // Animation configuration
  SLIDESHOW_INTERVAL: 3000, // 3 seconds for main slideshow
  ANIMATED_SLIDESHOW_INTERVAL: 400, // 400ms for animated slideshow (balanced between 234ms original and 1000ms)
  
  // Lazy loading configuration
  INTERSECTION_THRESHOLD: 0.1, // Load when 10% visible
  ROOT_MARGIN: '50px', // Start loading 50px before entering viewport
};

// Utility function to create intersection observer for lazy loading
export const createLazyLoadObserver = (callback: (entries: IntersectionObserverEntry[]) => void) => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }

  return new IntersectionObserver(callback, {
    threshold: PERFORMANCE_CONFIG.INTERSECTION_THRESHOLD,
    rootMargin: PERFORMANCE_CONFIG.ROOT_MARGIN,
  });
};

// Utility function to debounce expensive operations
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Memory management utilities
export const cleanupImageCache = () => {
  // Force garbage collection of unused images
  if ('gc' in window && typeof (window as any).gc === 'function') {
    (window as any).gc();
  }
};

// Performance monitoring
export const logPerformanceMetrics = () => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const paint = performance.getEntriesByType('paint');
    
    console.log('Performance Metrics:', {
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
      firstPaint: paint.find(p => p.name === 'first-paint')?.startTime,
      firstContentfulPaint: paint.find(p => p.name === 'first-contentful-paint')?.startTime,
    });
  }
};
